import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Eyebrow, GoldDivider } from "../../components/brand";
import SectionHeader from "../../components/shared/SectionHeader";
import { RESOURCES, RESOURCE_CATEGORIES } from "../../content/site";
import { ArrowUpRight, Clock, FileText, Lock } from "lucide-react";

/**
 * Premium Digital Library.
 *
 * Behavioral rules:
 *  - Hide resources with no `download_url` (PDF not yet uploaded).
 *  - Category tabs filter the visible library.
 *  - Every card links to a detail page /resources/:slug where the gated
 *    download form lives.
 */
export default function Resources() {
  const [category, setCategory] = useState("all");

  const publishedResources = useMemo(
    () => RESOURCES.filter((r) => r.download_url),
    [],
  );

  const filtered = useMemo(() => {
    if (category === "all") return publishedResources;
    return publishedResources.filter((r) => r.category === category);
  }, [category, publishedResources]);

  // Determine which category tabs actually have content — hide empty ones.
  const availableCategories = useMemo(() => {
    const cats = new Set(publishedResources.map((r) => r.category));
    return RESOURCE_CATEGORIES.filter((c) => c.key === "all" || cats.has(c.key));
  }, [publishedResources]);

  return (
    <div data-testid="page-resources">
      {/* Hero */}
      <section className="bg-altura-espresso text-altura-ivory pt-24 pb-20 relative noise overflow-hidden">
        <div className="pointer-events-none absolute -right-32 -bottom-40 h-[480px] w-[480px] rounded-full border border-altura-gold/15 opacity-70" />
        <div className="container-editorial relative z-10 max-w-4xl">
          <Eyebrow className="text-altura-gold">The Altura Global Library</Eyebrow>
          <h1 className="mt-4 font-editorial text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-altura-ivory">
            A premium digital library, built for global investors.
          </h1>
          <GoldDivider className="mt-6" />
          <p className="mt-6 text-altura-ivory/70 leading-relaxed max-w-2xl">
            Guides, checklists, and country briefings — curated by the Altura Global team. Enter your details to unlock any resource; we'll send a copy straight to your inbox and personally follow up with anything else you'd like to explore.
          </p>
        </div>
      </section>

      {/* Category tabs — hidden entirely when there's only "All Resources"
          (i.e. one published category); shown once ≥2 categories exist. */}
      {availableCategories.length > 1 && (
        <section className="bg-altura-ivory border-b border-altura-espresso/5 sticky top-24 z-30">
          <div className="container-editorial py-4">
            <div className="flex flex-wrap gap-2" data-testid="resource-category-tabs">
              {availableCategories.map((c) => {
                const active = c.key === category;
                return (
                  <button
                    key={c.key}
                    onClick={() => setCategory(c.key)}
                    className={`rounded-full px-5 py-2 text-xs uppercase tracking-[0.16em] transition-all ${
                      active
                        ? "bg-altura-espresso text-altura-ivory shadow-md"
                        : "border border-altura-espresso/15 text-altura-charcoal/70 hover:border-altura-gold hover:text-altura-espresso"
                    }`}
                    data-testid={`resource-tab-${c.key}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Library grid */}
      <section className="bg-altura-ivory pb-24 pt-14">
        <div className="container-editorial">
          {filtered.length === 0 ? (
            <div className="rounded-lg border border-altura-espresso/10 bg-white p-14 text-center">
              <div className="eyebrow mx-auto">Coming soon</div>
              <h3 className="mt-3 font-editorial text-2xl text-altura-espresso">More resources on the way.</h3>
              <p className="mt-3 text-altura-charcoal/70">
                Our editorial team is polishing new pieces for this category. Please check back soon.
              </p>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((r) => <ResourceCard key={r.key} r={r} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function ResourceCard({ r }) {
  return (
    <Link
      to={`/resources/${r.slug}`}
      className="group card-editorial overflow-hidden block transition-all hover:shadow-[0_30px_80px_-20px_rgba(58,31,20,0.2)]"
      data-testid={`resource-card-${r.key}`}
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-altura-beige/40">
        <img
          src={r.image}
          alt={r.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
        />
        <div className="absolute top-4 left-4">
          <span className="badge-soft bg-white/95 backdrop-blur">
            <FileText className="h-3 w-3" /> {r.category_label}
          </span>
        </div>
        {r.visibility === "gated" && (
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center gap-1 rounded-full border border-altura-gold/50 bg-altura-espresso/90 px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] text-altura-gold backdrop-blur">
              <Lock className="h-2.5 w-2.5" /> Gated
            </span>
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="font-editorial text-2xl text-altura-espresso leading-tight group-hover:text-altura-gold-dark transition-colors">
          {r.title}
        </h3>
        <p className="mt-3 text-sm text-altura-charcoal/70 leading-relaxed line-clamp-2">{r.description}</p>
        <div className="mt-5 flex items-center justify-between text-[11px] uppercase tracking-[0.16em] text-altura-charcoal/60">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3 w-3 text-altura-gold-dark" /> {r.reading_time}
          </span>
          <span className="inline-flex items-center gap-1 text-altura-espresso group-hover:text-altura-gold-dark">
            Open <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
